import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import {
  useCreateSocialNetwork,
  useCreateStore,
  useCreateStoreInvite,
  useGetBillingConfig,
  useGetMe,
  useGetTeamRoles,
  useUpdateStore,
  useUpdateStoreSettings,
  useUploadImage,
} from "@/api";
import { CreateSocialNetworkDtoPlatform } from "@/api/schemas/createSocialNetworkDtoPlatform";
import {
  buildRoleIdMap,
  getInvitableRoleDefinitions,
  getRoleDefinition,
} from "@/lib/settings/access";
import {
  BUSINESS_TYPE_OPTIONS,
  CATEGORY_OPTIONS,
  PAYMENT_GATEWAY_OPTIONS,
  SHIPPING_METHOD_KEYS,
  SHIPPING_METHOD_META,
  SMS_OPTION_KEYS,
  SMS_OPTION_META,
  SOCIAL_PLATFORM_DEFS,
  type ShopRoleKey,
  type SocialPlatformKey,
} from "@/lib/settings/constants";
import { formatCommissionRate, toAmount } from "@/lib/billing/constants";
import { formatPersianNumber } from "@/lib/format/persian";
import { INSTAGRAM_BOT_ENABLED } from "@/lib/instagram/constants";
import {
  createShopInfoSchema,
  type CreateShopInfoFormValues,
} from "@/lib/validation/shops.schemas";
import {
  shippingSettingsSchema,
  type ShippingSettingsFormValues,
} from "@/lib/validation/settings.schemas";
import { useSessionStore } from "@/stores/session.store";

export const CREATE_SHOP_STEPS = [
  "info",
  "socials",
  "shipping",
  "team",
  "payout",
  "review",
] as const;

export type CreateShopStep = (typeof CREATE_SHOP_STEPS)[number];

type PendingInvite = {
  phone: string;
  roleKey: ShopRoleKey;
};

type ReviewRow = {
  key: string;
  value?: string;
  valueKey?: string;
  settingsValueKey?: string;
  settingsValueKeys?: string[];
};
type ReviewGroup = {
  title: string;
  rows: ReviewRow[];
  onEdit: () => void;
};

function buildShippingMethods(values: ShippingSettingsFormValues) {
  return {
    post: values.shipping_post,
    courier: values.shipping_courier,
    pickup: values.shipping_pickup,
  };
}

function buildSocialPayload(platform: SocialPlatformKey, value: string, storeId: number) {
  const trimmed = value.replace(/^@/, "").trim();
  return {
    page_id: trimmed,
    link: value.startsWith("http") ? value : `https://${platform}.com/${trimmed}`,
    type: "channel",
    platform: CreateSocialNetworkDtoPlatform[platform],
    store_id: storeId,
  };
}

export function useCreateShopPage() {
  const navigate = useNavigate();
  const setActiveStoreId = useSessionStore((state) => state.setActiveStoreId);

  const [step, setStep] = useState<CreateShopStep>("info");
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [invitePhone, setInvitePhone] = useState("");
  const [selectedInviteRoleKey, setSelectedInviteRoleKey] = useState<ShopRoleKey | null>(null);
  const [socialValues, setSocialValues] = useState<Record<SocialPlatformKey, string>>({
    instagram: "",
    telegram: "",
    whatsapp: "",
    bale: "",
    rubika: "",
    eitaa: "",
  });
  const [primarySocial, setPrimarySocial] = useState<SocialPlatformKey | "">("");
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [maxReachedStepIndex, setMaxReachedStepIndex] = useState(0);

  const { data: meData } = useGetMe();
  const me = meData?.status === 200 ? meData.data.data : undefined;
  const isFirstShop = (me?.stores.length ?? 0) === 0;

  const infoForm = useForm<CreateShopInfoFormValues>({
    resolver: zodResolver(createShopInfoSchema),
    defaultValues: {
      name: "",
      category: "",
      business_type: "",
      warehouse_address: "",
      description: "",
      iban: "",
    },
  });

  const shippingForm = useForm<ShippingSettingsFormValues>({
    resolver: zodResolver(shippingSettingsSchema),
    defaultValues: {
      shipping_post: true,
      shipping_courier: true,
      shipping_pickup: false,
      sms_order_confirm: true,
      sms_tracking: true,
      sms_status_update: false,
    },
  });

  const watchedInfo = useWatch({ control: infoForm.control }) as CreateShopInfoFormValues | undefined;
  const watchedShipping = useWatch({ control: shippingForm.control }) as
    | ShippingSettingsFormValues
    | undefined;

  const { data: configData } = useGetBillingConfig({ query: { staleTime: Infinity } });
  const commissionRateLabel = formatCommissionRate(
    toAmount(configData?.status === 200 ? configData.data.data.commission_rate : 0.04),
  );

  // Owners have no global role.read; team-roles lists just the invitable store roles.
  const { data: rolesData } = useGetTeamRoles({ query: { staleTime: Infinity } });
  const roles = useMemo(
    () => (rolesData?.status === 200 ? rolesData.data.data : []),
    [rolesData],
  );
  const roleIdByKey = useMemo(() => buildRoleIdMap(roles), [roles]);

  const { mutateAsync: createStore } = useCreateStore();
  const { mutateAsync: uploadImage } = useUploadImage();
  const { mutateAsync: updateStore } = useUpdateStore();
  const { mutateAsync: createSocial } = useCreateSocialNetwork();
  const { mutateAsync: updateStoreSettings } = useUpdateStoreSettings();
  const { mutateAsync: createInvite } = useCreateStoreInvite();

  const stepIndex = CREATE_SHOP_STEPS.indexOf(step);
  const stepperProgressPercent =
    stepIndex <= 0 ? 0 : (stepIndex / (CREATE_SHOP_STEPS.length - 1)) * 100;
  const mobileProgressWidth = `${((stepIndex + 1) / CREATE_SHOP_STEPS.length) * 100}%`;

  const getStepState = (index: number): "done" | "current" | "upcoming" => {
    if (index < stepIndex) return "done";
    if (index === stepIndex) return "current";
    return "upcoming";
  };

  const canNavigateToStep = useCallback(
    (targetIndex: number) => targetIndex <= maxReachedStepIndex,
    [maxReachedStepIndex],
  );

  const goToStep = useCallback(
    (target: CreateShopStep) => {
      const targetIndex = CREATE_SHOP_STEPS.indexOf(target);
      if (targetIndex < 0 || targetIndex > maxReachedStepIndex) return;
      setStep(target);
    },
    [maxReachedStepIndex],
  );

  const goNext = useCallback(() => {
    const currentIndex = CREATE_SHOP_STEPS.indexOf(step);
    if (currentIndex < CREATE_SHOP_STEPS.length - 1) {
      const nextIndex = currentIndex + 1;
      setStep(CREATE_SHOP_STEPS[nextIndex]);
      setMaxReachedStepIndex((prev) => Math.max(prev, nextIndex));
    }
  }, [step]);

  const goBack = useCallback(() => {
    const currentIndex = CREATE_SHOP_STEPS.indexOf(step);
    if (currentIndex > 0) {
      setStep(CREATE_SHOP_STEPS[currentIndex - 1]);
    }
  }, [step]);

  const anySocialFilled = SOCIAL_PLATFORM_DEFS.some(
    (platform) => Boolean(socialValues[platform.key]?.trim()),
  );

  const handleLogoSelect = useCallback((file: File) => {
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  }, []);

  const handleNext = useCallback(() => {
    if (step === "info") {
      void infoForm.handleSubmit(() => goNext())();
      return;
    }
    if (step === "socials" && !anySocialFilled) return;
    if (step === "payout") {
      void infoForm.trigger("iban").then((isValid) => {
        if (isValid) goNext();
      });
      return;
    }
    goNext();
  }, [anySocialFilled, goNext, infoForm, step]);

  const categoryOptions = useMemo(
    () => CATEGORY_OPTIONS.map((option) => ({ value: option.value, labelKey: option.labelKey })),
    [],
  );

  const businessTypeOptions = useMemo(
    () => BUSINESS_TYPE_OPTIONS.map((option) => ({ value: option.value, labelKey: option.labelKey })),
    [],
  );

  const socialRows = useMemo(
    () =>
      SOCIAL_PLATFORM_DEFS.map((platform) => {
        const value = socialValues[platform.key] ?? "";
        const isFilled = Boolean(value.trim());
        const isPrimary = primarySocial === platform.key;
        return {
          key: platform.key,
          labelKey: platform.labelKey,
          placeholderKey: platform.placeholderKey,
          helperKey: platform.helperKey,
          colorToken: platform.colorToken,
          icon: platform.icon,
          value,
          isPrimary,
          isFilled,
          onChange: (nextValue: string) => {
            setSocialValues((current) => ({ ...current, [platform.key]: nextValue }));
            if (!primarySocial && nextValue.trim()) {
              setPrimarySocial(platform.key);
            }
            if (primarySocial === platform.key && !nextValue.trim()) {
              const fallback = SOCIAL_PLATFORM_DEFS.find(
                (item) => item.key !== platform.key && socialValues[item.key]?.trim(),
              );
              setPrimarySocial(fallback?.key ?? "");
            }
          },
          onSetPrimary: () => {
            if (!isFilled) return;
            setPrimarySocial(platform.key);
          },
        };
      }),
    [primarySocial, socialValues],
  );

  const shippingOptions = useMemo(() => {
    const values = watchedShipping ?? shippingForm.getValues();
    return SHIPPING_METHOD_KEYS.map((key) => ({
      key,
      titleKey: SHIPPING_METHOD_META[key].titleKey,
      descriptionKey: SHIPPING_METHOD_META[key].descKey,
      checked: values[`shipping_${key}` as keyof ShippingSettingsFormValues] as boolean,
      onToggle: () => {
        const field = `shipping_${key}` as keyof ShippingSettingsFormValues;
        shippingForm.setValue(field, !shippingForm.getValues(field));
      },
    }));
  }, [shippingForm, watchedShipping]);

  const smsOptions = useMemo(() => {
    const values = watchedShipping ?? shippingForm.getValues();
    return SMS_OPTION_KEYS.map((key) => ({
      key,
      titleKey: SMS_OPTION_META[key].titleKey,
      descriptionKey: SMS_OPTION_META[key].descKey,
      checked: values[key],
      onToggle: (checked: boolean) => shippingForm.setValue(key, checked),
    }));
  }, [shippingForm, watchedShipping]);

  const gatewayOptions = useMemo(
    () => PAYMENT_GATEWAY_OPTIONS.map((option) => ({ value: option.value, labelKey: option.labelKey })),
    [],
  );

  const inviteRoleCards = useMemo(
    () =>
      getInvitableRoleDefinitions().map((definition) => ({
        roleKey: definition.key,
        titleKey: definition.labelKey,
        descriptionKey: definition.descriptionKey,
        icon: definition.icon,
        selected: selectedInviteRoleKey === definition.key,
        onSelect: () => {
          setSelectedInviteRoleKey((current) =>
            current === definition.key ? null : definition.key,
          );
        },
      })),
    [selectedInviteRoleKey],
  );

  const canAddInvite = Boolean(invitePhone) && /^09\d{9}$/.test(invitePhone) && selectedInviteRoleKey !== null;

  const onAddInvite = useCallback(() => {
    if (!canAddInvite || selectedInviteRoleKey === null) return;
    setPendingInvites((current) => [
      ...current,
      { phone: invitePhone, roleKey: selectedInviteRoleKey },
    ]);
    setInvitePhone("");
    setSelectedInviteRoleKey(null);
  }, [canAddInvite, invitePhone, selectedInviteRoleKey]);

  const removeInvite = useCallback((index: number) => {
    setPendingInvites((current) => current.filter((_, i) => i !== index));
  }, []);

  const inviteLink = useMemo(() => {
    const roleParam = selectedInviteRoleKey ?? "operator";
    const origin = typeof window !== "undefined" ? window.location.origin : "https://sefano.ir";
    return `${origin}/invite/new?role=${encodeURIComponent(roleParam)}`;
  }, [selectedInviteRoleKey]);

  const invitedMembers = useMemo(
    () =>
      pendingInvites.map((invite, index) => {
        const definition = getRoleDefinition(invite.roleKey);
        return {
          phone: invite.phone,
          roleLabelKey: definition.labelKey,
          badgeColor: definition.badgeColor,
          avatarColor: definition.avatarColor,
          icon: definition.icon,
          onRemove: () => removeInvite(index),
        };
      }),
    [pendingInvites, removeInvite],
  );

  const isLastStep = step === "review";
  const showBack = stepIndex > 0;
  const nextDisabled = step === "socials" && !anySocialFilled;

  const infoValues = watchedInfo ?? infoForm.getValues();
  const displayName = infoValues.name.trim() || "create.preview.defaultName";

  const categoryLabelKey = categoryOptions.find((option) => option.value === infoValues.category)?.labelKey;
  const bizTypeLabelKey = businessTypeOptions.find(
    (option) => option.value === infoValues.business_type,
  )?.labelKey;

  const profileCompletionChecks = [
    Boolean(infoValues.name.trim()),
    Boolean(infoValues.category),
    Boolean(infoValues.business_type),
    anySocialFilled,
  ];
  const profileCompletion = Math.round(
    (profileCompletionChecks.filter(Boolean).length / profileCompletionChecks.length) * 100,
  );

  const connectedSocials = useMemo(
    () =>
      SOCIAL_PLATFORM_DEFS.map((platform) => {
        const filled = Boolean(socialValues[platform.key]?.trim());
        const isPrimary = primarySocial === platform.key && filled;
        return {
          key: platform.key,
          colorToken: platform.colorToken,
          icon: platform.icon,
          filled,
          isPrimary,
        };
      }),
    [primarySocial, socialValues],
  );

  const checklist = useMemo(
    () => [
      {
        done: Boolean(infoValues.name.trim()),
        labelKey: "create.checklist.basic",
      },
      {
        done: stepIndex > CREATE_SHOP_STEPS.indexOf("socials") || anySocialFilled,
        labelKey: "create.checklist.socials",
      },
      {
        done: stepIndex > CREATE_SHOP_STEPS.indexOf("shipping"),
        labelKey: "create.checklist.shipping",
      },
      {
        done: stepIndex > CREATE_SHOP_STEPS.indexOf("team"),
        labelKey: "create.checklist.team",
      },
      {
        done: Boolean(infoValues.iban),
        labelKey: "create.checklist.payout",
      },
    ],
    [anySocialFilled, infoValues.iban, infoValues.name, stepIndex],
  );

  const benefits = useMemo(
    () => [
      { iconKey: "link", labelKey: "create.benefits.orderLink" },
      { iconKey: "card", labelKey: "create.benefits.payment" },
      { iconKey: "bell", labelKey: "create.benefits.sms" },
      { iconKey: "chart", labelKey: "create.benefits.reports" },
    ],
    [],
  );

  const reviewGroups: ReviewGroup[] = useMemo(() => {
    const gatewayLabelKey = gatewayOptions.find(
      (option) => option.value === "zarinpal",
    )?.labelKey;

    const shippingTitleKeys = shippingOptions
      .filter((option) => option.checked)
      .map((option) => option.titleKey);

    const primaryPlatform = SOCIAL_PLATFORM_DEFS.find((platform) => platform.key === primarySocial);

    return [
      {
        title: "create.review.basic",
        onEdit: () => goToStep("info"),
        rows: [
          {
            key: "create.review.shopName",
            value: infoValues.name.trim() || undefined,
            valueKey: infoValues.name.trim() ? undefined : "create.preview.defaultName",
          },
          {
            key: "create.review.category",
            settingsValueKey: categoryLabelKey,
            valueKey: categoryLabelKey ? undefined : "create.review.notSelected",
          },
        ],
      },
      {
        title: "create.review.primarySocial",
        onEdit: () => goToStep("socials"),
        rows: [
          {
            key: "create.review.primaryChannel",
            settingsValueKey: primaryPlatform?.labelKey,
            valueKey: primaryPlatform ? undefined : "create.review.notSelected",
          },
        ],
      },
      {
        title: "create.review.shipping",
        onEdit: () => goToStep("shipping"),
        rows: [
          {
            key: "create.review.shippingMethods",
            settingsValueKeys: shippingTitleKeys.length > 0 ? shippingTitleKeys : undefined,
            valueKey: shippingTitleKeys.length > 0 ? undefined : "create.review.notSelected",
          },
          {
            key: "create.review.paymentGateway",
            settingsValueKey: gatewayLabelKey,
            valueKey: gatewayLabelKey ? undefined : "create.review.notSelected",
          },
        ],
      },
      {
        title: "create.review.team",
        onEdit: () => goToStep("team"),
        rows: [
          {
            key: "create.review.invitedCount",
            value: `${formatPersianNumber(pendingInvites.length)} نفر`,
          },
        ],
      },
      {
        title: "create.review.payout",
        onEdit: () => goToStep("payout"),
        rows: [
          {
            key: "create.review.iban",
            value: infoValues.iban || undefined,
            valueKey: infoValues.iban ? undefined : "create.review.ibanNotSet",
          },
          {
            key: "create.review.commissionRate",
            value: commissionRateLabel,
          },
        ],
      },
    ];
  }, [
    categoryLabelKey,
    commissionRateLabel,
    gatewayOptions,
    goToStep,
    infoValues.iban,
    infoValues.name,
    pendingInvites.length,
    primarySocial,
    shippingOptions,
  ]);

  const handleFinish = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const info = infoForm.getValues();
      const shipping = shippingForm.getValues();

      const storeResponse = await createStore({
        data: {
          name: info.name,
          category: info.category || undefined,
          business_type: info.business_type || undefined,
          warehouse_address: info.warehouse_address || undefined,
          description: info.description || undefined,
          iban: info.iban || undefined,
        },
      });

      if (storeResponse.status !== 201) return;

      const storeId = storeResponse.data.data.id;
      setActiveStoreId(storeId);

      if (logoFile) {
        const uploadResponse = await uploadImage({ data: { file: logoFile } });
        if (uploadResponse.status === 201) {
          await updateStore({
            id: storeId,
            data: { logo_url: uploadResponse.data.url },
          });
        }
      }

      await Promise.all(
        SOCIAL_PLATFORM_DEFS.map(async (platform) => {
          const value = socialValues[platform.key]?.trim();
          if (!value) return;
          await createSocial({
            data: buildSocialPayload(platform.key, value, storeId),
          });
        }),
      );

      await updateStoreSettings({
        storeId,
        data: {
          sms_order_confirm: shipping.sms_order_confirm,
          sms_tracking: shipping.sms_tracking,
          sms_status_update: shipping.sms_status_update,
          shipping_methods: buildShippingMethods(shipping),
        },
      });

      await Promise.all(
        pendingInvites.map(async (invite) => {
          const roleId = roleIdByKey.get(invite.roleKey);
          if (!roleId) return;
          await createInvite({
            storeId,
            data: { phone: invite.phone, role_id: roleId },
          });
        }),
      );

      void navigate({ to: INSTAGRAM_BOT_ENABLED ? "/shops/instagram" : "/" });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    createInvite,
    createSocial,
    createStore,
    infoForm,
    logoFile,
    navigate,
    pendingInvites,
    roleIdByKey,
    setActiveStoreId,
    shippingForm,
    socialValues,
    updateStore,
    updateStoreSettings,
    uploadImage,
  ]);

  return {
    step,
    setStep: goToStep,
    stepIndex,
    stepperProgressPercent,
    mobileProgressWidth,
    getStepState,
    canNavigateToStep,
    goNext: handleNext,
    goBack,
    isFirstShop,
    isLastStep,
    showBack,
    nextDisabled,
    isSubmitting,
    commissionRateLabel,
    infoForm,
    shippingForm,
    socialRows,
    anySocialFilled,
    shippingOptions,
    smsOptions,
    gatewayOptions,
    inviteRoleCards,
    invitePhone,
    onInvitePhoneChange: setInvitePhone,
    inviteLink,
    canAddInvite,
    onAddInvite,
    invitedMembers,
    hasInvites: invitedMembers.length > 0,
    inviteCountLabel: formatPersianNumber(pendingInvites.length),
    logoPreview,
    onLogoSelect: handleLogoSelect,
    categoryOptions,
    businessTypeOptions,
    displayName,
    categoryLabelKey,
    bizTypeLabelKey,
    warehouseAddress: infoValues.warehouse_address ?? "",
    description: infoValues.description ?? "",
    profileCompletion,
    connectedSocials,
    checklist,
    benefits,
    reviewGroups,
    handleFinish,
    watchedName: infoValues.name ?? "",
  };
}
